import { LOCATION_CHANGE } from '../../../../../shared/frontend/libs/router/redux-store';
import { call, put, takeLatest } from 'redux-saga/effects';
import { TBoundAction } from '../../../../../shared/frontend/scripts/actions/types/action';
import { navigateToWithPageRefresh } from '../../../../../shared/frontend/libs/history';
import { apiServiceAsync } from '../services/apiService';
import { matchRoutePath } from '../../../../../shared/frontend/libs/router/router-helpers';
import _ from 'lodash';
import { namedRoutes } from '../bundles/routes';

//
// Actions
//

type TUserClientState = {};

// user profile
type TViewUserProfileRequestedParams = {
  name: string;
};

type TViewUserProfileSucceededParams = TViewUserProfileRequestedParams & {
  results: any;
};

type TViewUserProfileFailedParams = TViewUserProfileRequestedParams & {
  error: any;
};

// user characters
type TViewUserCharactersRequestedParams = {
  name: string;
};

type TViewUserCharactersSucceededParams = TViewUserCharactersRequestedParams & {
  results: any;
};

type TViewUserCharactersFailedParams = TViewUserCharactersRequestedParams & {
  error: any;
};

export const enum ActionTypes {
  SET_VIEW_USER_PROFILE_REQUESTED = 'USER/VIEW_USER_PROFILE_REQUESTED',
  SET_VIEW_USER_PROFILE_SUCCEEDED = 'USER/VIEW_USER_PROFILE_SUCCEEDED',
  SET_VIEW_USER_PROFILE_FAILED = 'USER/VIEW_USER_PROFILE_FAILED',

  SET_VIEW_USER_CHARACTERS_REQUESTED = 'USER/VIEW_USER_CHARACTERS_REQUESTED',
  SET_VIEW_USER_CHARACTERS_SUCCEEDED = 'USER/VIEW_USER_CHARACTERS_SUCCEEDED',
  SET_VIEW_USER_CHARACTERS_FAILED = 'USER/VIEW_USER_CHARACTERS_FAILED',

  REDIRECT_TO_LOGIN = 'USER/REDIRECT_TO_LOGIN'
}

// user profile
type TActionViewUserProfileRequested = TBoundAction<
  ActionTypes.SET_VIEW_USER_PROFILE_REQUESTED,
  TViewUserProfileRequestedParams
>;
type TActionViewUserProfileSucceeded = TBoundAction<
  ActionTypes.SET_VIEW_USER_PROFILE_SUCCEEDED,
  TViewUserProfileSucceededParams
>;
type TActionViewUserProfileFailed = TBoundAction<
  ActionTypes.SET_VIEW_USER_PROFILE_FAILED,
  TViewUserProfileFailedParams
>;

// user characters
type TActionViewUserCharactersRequested = TBoundAction<
  ActionTypes.SET_VIEW_USER_CHARACTERS_REQUESTED,
  TViewUserCharactersRequestedParams
>;
type TActionViewUserCharactersSucceeded = TBoundAction<
  ActionTypes.SET_VIEW_USER_CHARACTERS_SUCCEEDED,
  TViewUserCharactersSucceededParams
>;
type TActionViewUserCharactersFailed = TBoundAction<
  ActionTypes.SET_VIEW_USER_CHARACTERS_FAILED,
  TViewUserCharactersFailedParams
>;

// redirect to login
type TActionRedirectToLogin = TBoundAction<ActionTypes.REDIRECT_TO_LOGIN, string>;

type TSupportedActions =
  | TActionViewUserProfileRequested
  | TActionViewUserProfileSucceeded
  | TActionViewUserProfileFailed
  | TActionViewUserCharactersRequested
  | TActionViewUserCharactersSucceeded
  | TActionViewUserCharactersFailed
  | TActionRedirectToLogin;

export type TActions = {
  viewUserProfileRequested(params: TViewUserProfileRequestedParams): TActionViewUserProfileRequested;
  viewUserProfileSucceeded(params: TViewUserProfileSucceededParams): TActionViewUserProfileSucceeded;
  viewUserProfileFailed(params: TViewUserProfileFailedParams): TActionViewUserProfileFailed;

  viewUserCharactersRequested(params: TViewUserCharactersRequestedParams): TActionViewUserCharactersRequested;
  viewUserCharactersSucceeded(params: TViewUserCharactersSucceededParams): TActionViewUserCharactersSucceeded;
  viewUserCharactersFailed(params: TViewUserCharactersFailedParams): TActionViewUserCharactersFailed;

  redirectToLogin(params: string): any;
};

export const actions: TActions = {
  //
  // view user profile
  //
  viewUserProfileRequested(params: TViewUserProfileRequestedParams) {
    return { type: ActionTypes.SET_VIEW_USER_PROFILE_REQUESTED, payload: params };
  },

  viewUserProfileSucceeded(params: TViewUserProfileSucceededParams) {
    return {
      type: ActionTypes.SET_VIEW_USER_PROFILE_SUCCEEDED,
      payload: params
    };
  },

  viewUserProfileFailed(params: TViewUserProfileFailedParams) {
    return {
      type: ActionTypes.SET_VIEW_USER_PROFILE_FAILED,
      payload: params
    };
  },

  //
  // view user characters
  //
  viewUserCharactersRequested(params: TViewUserCharactersRequestedParams) {
    return { type: ActionTypes.SET_VIEW_USER_CHARACTERS_REQUESTED, payload: params };
  },

  viewUserCharactersSucceeded(params: TViewUserCharactersSucceededParams) {
    return {
      type: ActionTypes.SET_VIEW_USER_CHARACTERS_SUCCEEDED,
      payload: params
    };
  },

  viewUserCharactersFailed(params: TViewUserCharactersFailedParams) {
    return {
      type: ActionTypes.SET_VIEW_USER_CHARACTERS_FAILED,
      payload: params
    };
  },

  redirectToLogin(str: string) {
    return {
      type: ActionTypes.REDIRECT_TO_LOGIN,
      payload: str
    };
  }
};

//
// Reducer
//

function getInitialState() {
  return {
    viewUserProfile: {
      request: {},
      response: {},
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },
    viewUserCharacters: {
      request: {},
      response: {},
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    }
  };
}

export function reducer(state = getInitialState(), action: TSupportedActions): Partial<TUserClientState> {
  switch (action.type) {
    // view user profile
    case ActionTypes.SET_VIEW_USER_PROFILE_REQUESTED: {
      return {
        ...state,
        viewUserProfile: {
          ...state.viewUserProfile,
          request: action.payload,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case ActionTypes.SET_VIEW_USER_PROFILE_SUCCEEDED: {
      return {
        ...state,
        viewUserProfile: {
          ...state.viewUserProfile,
          request: {
            name: action.payload.name
          },
          response: action.payload.results,
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };
    }

    case ActionTypes.SET_VIEW_USER_PROFILE_FAILED: {
      return {
        ...state,
        viewUserProfile: {
          ...state.viewUserProfile,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    // view user characters
    case ActionTypes.SET_VIEW_USER_CHARACTERS_REQUESTED: {
      return {
        ...state,
        viewUserCharacters: {
          ...state.viewUserCharacters,
          request: action.payload,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case ActionTypes.SET_VIEW_USER_CHARACTERS_SUCCEEDED: {
      return {
        ...state,
        viewUserCharacters: {
          ...state.viewUserCharacters,
          request: {
            name: action.payload.name
          },
          response: action.payload.results,
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };
    }

    case ActionTypes.SET_VIEW_USER_CHARACTERS_FAILED: {
      return {
        ...state,
        viewUserCharacters: {
          ...state.viewUserCharacters,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case ActionTypes.REDIRECT_TO_LOGIN: {
      navigateToWithPageRefresh(`/login?next=${encodeURIComponent(action.payload)}`);
      return state;
    }

    case LOCATION_CHANGE as any: {
      const match = matchRoutePath({ path: namedRoutes.UserProfile, end: false }, (action.payload as any).location.pathname);

      const user: any = !_.isEmpty(state.viewUserProfile.response) ? state.viewUserProfile.response : null;

      if (user && match && user.name === match.params.name) {
        return state;
      }

      return getInitialState();
    }
  }

  return state;
}

//
// Sagas
//

//
// view user profile
//
function* viewUserProfileRequested(action: TActionViewUserProfileRequested): any {
  const { name } = action.payload;
  try {
    const results = yield call(apiServiceAsync.viewUserProfile, { name });
    if (results) {
      yield put(
        actions.viewUserProfileSucceeded({
          name,
          results: results.viewUserProfileApi
        })
      );
    } else {
      yield put(actions.redirectToLogin(window.location.pathname));
    }
  } catch (error) {
    yield put(actions.viewUserProfileFailed({ name, error }));
  }
}

function* watchViewUserProfileRequested(): any {
  yield takeLatest(ActionTypes.SET_VIEW_USER_PROFILE_REQUESTED, viewUserProfileRequested);
}

//
// view user characters
//
function* viewUserCharactersRequested(action: TActionViewUserCharactersRequested): any {
  const { name } = action.payload;
  try {
    const results = yield call(apiServiceAsync.viewUserCharacters, { name });
    yield put(
      actions.viewUserCharactersSucceeded({
        name,
        results: results.viewUserCharactersApi
      })
    );
  } catch (e) {
    yield put(actions.viewUserCharactersFailed({ name, error: e.toString() }));
  }
}

function* watchViewUserCharactersRequested(): any {
  yield takeLatest(ActionTypes.SET_VIEW_USER_CHARACTERS_REQUESTED, viewUserCharactersRequested);
}

//
// saga exports
//
export const sagas = [watchViewUserProfileRequested(), watchViewUserCharactersRequested()];
